<template>
  <div class="page-container">
    <!-- Page Header -->
    <page-header
      icon="mdi-silverware-fork-knife"
      :title="$t('restaurants')"
      subtitle="Pregledajte dostupne restorane"
      icon-color="primary"
    >
    </page-header>

    <!-- Toolbar -->
    <page-toolbar v-if="!device.isMobile" :searchable="true" :search.sync="search">
      <template #filters>
        <!-- Add any filters here if needed -->
      </template>
    </page-toolbar>

    <!-- Mobile Filters -->
    <template v-else>
      <mobile-filters
        :filters="mobileFilterConfig"
        @update:filter="handleMobileFilterUpdate"
        @apply="onFiltersApply"
        @reset="resetFilters"
      />
    </template>

    <!-- Content Area -->
    <div :class="device.isMobile ? 'px-0 mt-2' : 'px-6 mt-2'">
      <v-data-table
        v-if="!device.isMobile"
        :headers="headers"
        :items="filteredRestaurants"
        :loading="loading"
        :items-per-page="10"
        class="ordera-box-shadow striped-table"
      >
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center">
            <v-icon left color="primary" size="20">mdi-map-marker</v-icon>
            {{ toLatin(item.name) }}
          </div>
        </template>

        <template v-slot:item.menu_types_id="{ item }">
          <v-chip small color="blue" text-color="white">
            {{ getMenuTypeName(item.menu_types_id) }}
          </v-chip>
        </template>

        <template v-slot:item.is_credit="{ item }">
          <v-icon :color="item.is_credit ? 'success' : 'error'">
            {{ item.is_credit ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:item.price="{ item }">
          <span class="grey--text text--darken-1">N/A</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <circle-button
            icon="mdi-information"
            color="primary"
            :tooltip="$t('contact_info')"
            @click="openContactDialog(item)"
          />
        </template>
      </v-data-table>

      <!-- Mobile: Cards -->
      <mobile-data-cards
        v-else
        :headers="mobileHeaders"
        :items="filteredRestaurants"
        :fields-per-row="1"
        :bold-fields="['name']"
        @click:row="viewRestaurant"
      >
        <template v-slot:item.name="{ item }">
          <div class="font-weight-medium">{{ toLatin(item.name) }}</div>
        </template>

        <template v-slot:item.menu_types_id="{ item }">
          <v-chip x-small color="blue" text-color="white">
            {{ getMenuTypeName(item.menu_types_id) }}
          </v-chip>
        </template>

        <template v-slot:item.is_credit="{ item }">
          <v-icon small :color="item.is_credit ? 'success' : 'error'">
            {{ item.is_credit ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:item.price="{ item }">
          <span class="grey--text text--darken-1">N/A</span>
        </template>
      </mobile-data-cards>
    </div>

    <!-- Restaurant Contact Dialog -->
    <restaurant-contact-dialog v-model="contactDialog" :restaurant="selectedRestaurant" />
  </div>
</template>

<script>
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileFilters from "@/components/global/MobileFilters.vue";
import hasMobileFilters from "@/mixins/hasMobileFilters";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import RestaurantContactDialog from "@/components/customer/restaurants/RestaurantContactDialog.vue";
import companyService from "@/services/company";
import menuService from "@/services/menu";

export default {
  name: "CustomerRestaurantsPage",
  mixins: [hasMobileFilters],
  components: {
    PageHeader,
    PageToolbar,
    MobileFilters,
    MobileDataCards,
    RestaurantContactDialog,
  },
  data() {
    return {
      search: "",
      contactDialog: false,
      selectedRestaurant: null,
      restaurants: [],
      menuTypes: [],
      loading: false,
    };
  },
  computed: {
    device() {
      return this.$store.state.device;
    },
    companyId() {
      return this.$store.state.company?.id;
    },
    filteredRestaurants() {
      if (!this.search) return this.restaurants;
      const s = this.search.toLowerCase();
      return this.restaurants.filter((r) => r.name?.toLowerCase().includes(s));
    },
    headers() {
      return [
        { text: "ID", value: "id", sortable: false, width: "60px" },
        { text: this.$t("name"), value: "name", sortable: true },
        {
          text: this.$t("menu_type"),
          value: "menu_types_id",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("credits"),
          value: "is_credit",
          sortable: false,
          align: "center",
        },
        { text: this.$t("price"), value: "price", sortable: false, align: "center" },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "center",
          width: "80px",
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("name"), value: "name" },
        { text: this.$t("menu_type"), value: "menu_types_id" },
        { text: this.$t("credits"), value: "is_credit" },
        { text: this.$t("price"), value: "price" },
      ];
    },
    mobileFilterDefs() {
      return {
        search: {
          type: "text",
          model: "search",
          label: this.$t("search"),
          icon: "mdi-magnify",
        },
      };
    },
  },
  created() {
    this.loadRestaurants();
  },
  methods: {
    async loadRestaurants() {
      if (!this.companyId) return;
      this.loading = true;
      try {
        const [restaurantsRes, menuTypesRes] = await Promise.all([
          companyService.getCompanyRestaurants(this.companyId),
          menuService.getMenuTypes(),
        ]);
        this.restaurants = restaurantsRes.data || [];
        this.menuTypes = menuTypesRes.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    getMenuTypeName(id) {
      const mt = this.menuTypes.find((t) => t.id === id);
      return mt ? this.toLatin(mt.name) : id;
    },
    onFiltersApply() {},
    resetFilters() {
      this.search = "";
    },
    viewRestaurant(restaurant) {
      // TODO: Navigate to restaurant menu page
    },
    openContactDialog(restaurant) {
      this.selectedRestaurant = restaurant;
      this.contactDialog = true;
    },
  },
};
</script>
